/*
 * COPYRIGHT (c) 2024
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */
import React, { useContext } from 'react';
import { DataContextProvider } from '@forge/data-connector';
import { Button, Icon } from '@scuf/common';
import { useTranslation } from '@scuf/localization';

import descriptors from '../../locales/en-US/descriptors';
import IEnergyManagementProps from '../DashboardKPIWidgets/IEnergyManagementProps';
import {
  ApiContext,
  ContextProvider,
  updateApiConfig
} from '../../context/ApiContext';

import './CreateViewBaselineTheme.scss';
import { UtilityBasicConfiguration } from './UtilityBillBaseline/Configuration/BasicConfiguration';
import { BaselineConfigProvider } from '../../context/BaselineConfigContext/BaselineConfigContext';
import { withLocalization } from '../../locales/withLocalization';

interface ICreateViewBaselineProps {
  baselineType: 'utility' | 'sub-meter';
  utilityType: 'electricity' | 'gas';
  orgId: string;
  siteId: string;
  equipmentId: string;
  equipmentName: string;
  viewMode: 'create' | 'view';
}

const CreateViewBaselineContent: React.FC<ICreateViewBaselineProps> = (
  props: ICreateViewBaselineProps
) => {
  const { baselineType, utilityType, viewMode } = props;

  const [t] = useTranslation();
  const baselineConfigDesc = descriptors.baseline;

  const apiContext = useContext(ApiContext);
  const { onBacktoDashboardClick } = apiContext!;

  const onBackarrowClick = () => {
    if (onBacktoDashboardClick) {
      onBacktoDashboardClick();
    }
  };

  return (
    <div className="create-view-baseline-wrapper">
      <div className="create-view-baseline-header">
        <div className="create-view-baseline-header-title-wrapper">
          <div className="back-icon-wrapper">
            <Icon
              root="common"
              name="arrow-left"
              size="medium"
              onClick={() => onBackarrowClick()}
            />
          </div>
          <div className="title">{t(baselineConfigDesc.baseline)}</div>
        </div>
        <div className="create-view-baseline-header-action-buttons">
          <Button content="Save" disabled />
        </div>
      </div>
      <div className="create-view-baseline-content">
        <div className="create-view-baseline-content-left">
          {baselineType === 'utility' ? (
            <UtilityBasicConfiguration
              utilityType={utilityType}
              viewMode={viewMode}
            />
          ) : (
            <></>
          )}
        </div>
        <div className="create-view-baseline-content-separator"></div>
        <div className="create-view-baseline-content-right"></div>
      </div>
    </div>
  );
};

export interface ICreateViewBaselinePageProps
  extends Pick<IEnergyManagementProps, 'connectorConfig' | 'emaasContext'>,
    ICreateViewBaselineProps {}

const CreateViewBaselinePage = (props: ICreateViewBaselinePageProps) => {
  const { connectorConfig, emaasContext, ...restProps } = props;

  return (
    <ContextProvider
      value={updateApiConfig({
        connectorConfig,
        ...emaasContext
      })}
    >
      <DataContextProvider config={connectorConfig}>
        <BaselineConfigProvider>
          <CreateViewBaselineContent {...restProps} />
        </BaselineConfigProvider>
      </DataContextProvider>
    </ContextProvider>
  );
};

export default withLocalization<ICreateViewBaselinePageProps>(
  CreateViewBaselinePage
);
